import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import FileInput from "app/components/InputFile";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../../redux/slice";
import {
  selectform,
  selectIsLoading,
  selectIsAddModalOpen,
  selectUploadedFile,
  selectUploadedFileError,
} from "../../redux/selector";

import { formState, UploadedFile } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof formState;
  label: string;
  type: "select" | "text" | "input";
  inputType?: string;
};

const B = "BOOKS."

const FIELDS: FieldConfig[] = [
  { key: "formDesc",  label: ".FORM_NAME", type: "text" },
];

const REQUIRED_FIELDS: Array<keyof formState> = ["formDesc", "fileName"];

const AddFormModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();

  const form = useSelector(selectform);
  const isLoading = useSelector(selectIsLoading);
  const isOpen = useSelector(selectIsAddModalOpen);
  const uploadedFile = useSelector(selectUploadedFile);
  const uploadedFileError = useSelector(selectUploadedFileError);

  const onClose = () => {
    if (isLoading) return;
    dispatch(actions.closeAddModal());
  };

  const placeholder = (label: string, type: "select" | "text" | "input") =>
    t(
      `${
        B
      }PLACEHOLDER${label}`
    );

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(String(form[key]).trim()));

  const onChange = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
  key: keyof formState
) => {
    dispatch(actions.setFormField({ name: key, value: event.target.value }));
  };

  const onFileChange = (file: UploadedFile | null) => {
    dispatch(actions.setUploadedFile(file));
    dispatch(actions.setFormField({ name: "fileName", value: file?.name || "" }));
  };

  const onSubmit = () => {
    dispatch(actions.craeteForm(form));
    dispatch(actions.resetForm());
    dispatch(actions.closeAddModal());
  };


  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box className={styles.modal}>
          <Box className={styles.header}>
            <Typography className={styles.title}>
              {t(`BOOKS.ADD_TITLE`)}
            </Typography>
            <IconButton
              className={styles.closeButton}
              onClick={onClose}
              aria-label={t("BUTTONS.CANCEL")}
              disabled={isLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className={styles.body}>
            {FIELDS.map(({ key, label, type, inputType }) => (
              <Box key={key} className={styles.field}>
                <Typography className={styles.label}>
                  {t(`${B}FORMS${label}`)}
                </Typography>
                <TextField
                  className={styles.input}
                  fullWidth
                  select={type === "select"}
                  variant="outlined"
                  placeholder={placeholder(label, type)}
                  InputProps={{ notched: false }}
                  {...(type === "select" ? {
                    SelectProps: {
                      displayEmpty: true,
                      IconComponent: DropdownIcon,
                      renderValue: (value) =>
                        (value as string) || placeholder(label, "select"),
                    },
                  } : {})}
                  type={inputType}
                  disabled={isLoading}
                  {...(inputType !== "file" && { value: form[key] })}
                  onChange={(e)=>onChange(e,key)}
                >
                  {type === "select" ? (
                    <MenuItem value="">{placeholder(label, "select")}</MenuItem>
                  ):[]}
                </TextField>
              </Box>
            ))}

            <Box className={styles.field}>
              <Typography className={styles.label}>
                {t(`${B}FORMS.FILE_NAME`)}
              </Typography>
              <FileInput
                file={uploadedFile}
                error={uploadedFileError}
                dispatchFileChange={onFileChange}
                dispatchFileError={(x) => dispatch(actions.setUploadedFileError(x))}
              />
            </Box>
          </Box>

          <Box className={styles.footer}>
            <Button
              className={styles.cancelButton}
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
            >
              {t("BUTTONS.CANCEL")}
            </Button>
            <Button
              className={styles.addButton}
              variant="contained"
              disableElevation
              disabled={!isValid || isLoading}
              onClick={()=>{onSubmit()}}
            >
              {t("BUTTONS.ADD")}
            </Button>
          </Box>
      </Box>
    </Modal>
  );
};

export default AddFormModal;